import type { SortMethodType } from '@sdk/schedule/types/SortMethodType';

import type { DynamicGroup, DayPartGroup } from '../../types/GroupsArrayType';
import type { RowType } from '../../types/RowType';
import type {
  GetDayPartGroupDisplayNameType,
  ScheduleFilters,
  OptionsFilterType,
} from '../../types';
import { HEADER_TEXT_HEIGHT, SUMMARY_ROW_HEIGHT } from '../constants';

import getUserRows from './getUserRows';

type Config = {
  groupMethod: SortMethodType;
  personasDict: {
    [key in string]: any;
  };
  tags: Array<{
    id: number;
    name: string;
  }>;
  eventHeight: number;
  hideEmptyRows: boolean;
  showSummary: boolean;
  getDayPartGroupDisplayName: GetDayPartGroupDisplayNameType;
  scheduleFilters: ScheduleFilters;
  rowIds: { [key in string]: boolean };
  eventNumberOfLines: number;
  optionsFilter: OptionsFilterType;
};

export default function makeGroupedRows(
  groups: Array<DynamicGroup>,
  config: Config
): Array<RowType> {
  const rows = [];

  groups.forEach((group) => {
    if (!shouldDisplayGroup(group, config)) {
      return;
    }

    const { name, color } = getNameAndColor(group, config);
    rows.push({
      id: `GroupHeader_${name}_${group.key}`,
      rowType: 'HeaderTextRow',
      rowData: {
        text: name,
        color,
        size: HEADER_TEXT_HEIGHT,
      },
    });

    group.usersArray.forEach((user) => {
      rows.push(...getUserRows(user, group, config));
    });

    if (config.showSummary) {
      rows.push({
        id: `GroupSummary_${name}_${group.key}`,
        rowType: 'SummaryRow',
        rowData: {
          size: SUMMARY_ROW_HEIGHT,
          totalsPerDay: group.totalsPerDay,
        },
      });
    } else if (rows.length) {
      rows[rows.length - 1].rowData.withBottomSeparator = true;
    }
  });

  return rows;
}
type NameAndColor = {
  name: string;
  color?: string;
};
function getNameAndColor(group: DynamicGroup, config: Config): NameAndColor {
  if (config.groupMethod === 'tag') {
    if (group.type === 'noTags') {
      return { name: 'No tags' };
    }

    const tag = config.tags.find((t) => t.id === group.key);
    return { name: tag?.name };
  }

  if (config.groupMethod === 'group' && group.type === 'noGroups') {
    return { name: 'No groups' };
  }

  if (config.groupMethod === 'dayPart') {
    if (group.type === 'otherDayPart') {
      return { name: 'No day part' };
    }
    return { name: config.getDayPartGroupDisplayName({ group: group as DayPartGroup }) };
  }

  const { name, color } = config.personasDict[group.key];
  return { name, color };
}

function shouldDisplayGroup(group: DynamicGroup, config: Config): boolean {
  const {
    scheduleFilters: { positionIds, locationIds, groupIds, tagIds, dayPartIds },
    groupMethod,
  } = config;

  if (groupMethod === 'position') {
    return !positionIds.length || positionIds.includes(group.key as number);
  }

  if (groupMethod === 'location') {
    return !locationIds.length || locationIds.includes(group.key as number);
  }

  if (groupMethod === 'group') {
    return !groupIds.length || groupIds.includes(group.key as number);
  }

  if (groupMethod === 'tag') {
    return !tagIds.length || tagIds.includes(group.key as number);
  }

  if (groupMethod === 'dayPart') {
    return !dayPartIds.length || dayPartIds.includes(group.key as number);
  }

  return true;
}
